import { Modal } from "antd";
import { createContext, ReactNode, useContext, useRef, useState } from "react";
import styled from "styled-components";
import PictureFalls from "../components/PictureFalls";
import { dataCall, deleteCall, uploadCall } from "../tsrpc";
import {
  ContextType,
  DataProvider,
  DataType,
  LoadHandler,
} from "./DataProvider";
import { config } from "../config";

const SelectContainer = styled.div`
  height: 75vh;
  overflow-y: auto;
`;

export interface PictureData extends DataType {
  _id: string;
  key: string;
  name: string;
  md5: string;
  ext: ".jpg" | ".png" | ".jpeg" | ".gif" | string;
}

export interface PictureContext extends ContextType<PictureData> {
  urls: Record<string, string>;
  select: (value?: string, onChange?: (value: string) => void) => void;
  cache: (id: string) => Promise<void>;
  upload: (
    files: FileList
  ) => Promise<{ succeed: string[]; replaced: string[]; errored: string[] }>;
  replace: (data: PictureData, file: File) => Promise<void>;
  rename: (id: string, name: string) => Promise<void>;
  $delete: (data: PictureData) => Promise<void>;
}

const _PictureDataContext = createContext<ContextType<PictureData> | null>(
  null
);
const PictureDataContext = createContext<PictureContext | null>(null);

export const PictureDataProvider = ({ children }: { children: ReactNode }) => (
  <DataProvider
    context={_PictureDataContext}
    collection="pictures"
    sort={{ _id: -1 }}
    limit={24}
  >
    <InnerPictureDataProvider>{children}</InnerPictureDataProvider>
  </DataProvider>
);

const InnerPictureDataProvider = ({ children }: { children: ReactNode }) => {
  const context = useContext(_PictureDataContext);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [visible, setVisible] = useState(false);
  const [value, setValue] = useState<string | undefined>();
  const onChangeRef = useRef<(value: string) => void | undefined>();

  const select = (value?: string, onChange?: (value: string) => void) => {
    setVisible(true);
    setValue(value);
    onChangeRef.current = onChange;
  };

  const load: LoadHandler<PictureData> = async (filter, reset = false) => {
    if (!context) return [];
    const res = await context.load(filter, reset);
    for (const pic of res)
      if (!urls[pic._id])
        setUrls((prev) => ({
          ...prev,
          [pic._id]: `${config.picture_url}${pic.md5}${pic.ext}`,
        }));
    return res;
  };

  const cache = async (id: string) => {
    if (urls[id]) return;
    let pic = context?.data.find((d) => d._id === id);
    if (!pic) {
      const res = await dataCall("editor/DataFind", {
        collection: "pictures",
        filter: { _id: id },
      });
      if (!res.isSucc) throw res.err;
      pic = res.res.data[0];
    }
    setUrls((prev) => ({
      ...prev,
      [id]: `${config.picture_url}${pic!.md5}${pic!.ext}`,
    }));
  };

  const upload = async (files: FileList) => {
    const succeed: string[] = [],
      errored: string[] = [],
      replaced: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const name = file.name.slice(0, file.name.lastIndexOf("."));
      const ext = file.name.slice(file.name.lastIndexOf("."));
      const resFind = await dataCall("editor/DataFind", {
        collection: "pictures",
        filter: { name: name },
      });
      if (!resFind.isSucc) {
        errored.push(name);
        continue;
      }
      const exist = resFind.res.data[0];
      if (exist) {
        const resDelete = await deleteCall(`images/${exist.md5}${exist.ext}`);
        if (!resDelete.isSucc) {
          errored.push(name);
          continue;
        }
      }
      const resUpload = await uploadCall("images", file);
      if (!resUpload.isSucc) {
        errored.push(name);
        continue;
      }
      if (exist) {
        const resUpdate = await dataCall("editor/DataUpdate", {
          collection: "pictures",
          filter: { _id: exist._id },
          update: {
            $set: { md5: resUpload.res.md5, ext },
          },
        });
        if (!resUpdate.isSucc) {
          errored.push(name);
          continue;
        }
        replaced.push(name);
      } else {
        const resInsert = await dataCall("editor/DataInsert", {
          collection: "pictures",
          data: { name: name, md5: resUpload.res.md5, ext: ext },
        });
        if (!resInsert.isSucc) {
          errored.push(name);
          continue;
        }
      }
      succeed.push(name);
    }
    return { succeed, replaced, errored };
  };

  const replace = async (data: PictureData, file: File) => {
    if (!context) return;
    const usage = await dataCall("editor/DataFind", {
      collection: "pictures",
      filter: { md5: data.md5 },
    });
    if (!usage.isSucc) return;
    if (usage.res.data.length === 1) {
      const resDelete = await deleteCall(`images/${data.md5}${data.ext}`);
      if (!resDelete.isSucc) return;
    }
    const resUpload = await uploadCall("images", file);
    if (!resUpload.isSucc) return;
    await context.update(
      { _id: data._id },
      {
        $set: {
          md5: resUpload.res.md5,
          ext: file.name.slice(file.name.lastIndexOf(".")),
        },
      }
    );
  };

  const rename = async (id: string, name: string) => {
    await context?.update({ _id: id }, { $set: { name } });
  };

  const $delete = async (data: PictureData) => {
    if (!context) return;
    const resDelete = await deleteCall(`images/${data.md5}${data.ext}`);
    if (!resDelete.isSucc) return;
    await context.remove(data._id);
  };

  return (
    <PictureDataContext.Provider
      value={
        context
          ? {
              ...context,
              urls,
              select,
              load,
              cache,
              upload,
              replace,
              rename,
              $delete,
            }
          : null
      }
    >
      {children}
      <Modal
        zIndex={990}
        open={visible}
        centered
        width="60%"
        closable={false}
        maskClosable={false}
        okButtonProps={{ disabled: !value }}
        okText="确定"
        cancelText="取消"
        onOk={async () => {
          onChangeRef.current?.(value || "");
          setVisible(false);
        }}
        onCancel={() => setVisible(false)}
      >
        <SelectContainer>
          <PictureFalls
            title="选择图片"
            columnWidth={192}
            allowSelect
            selected={value}
            onSelect={setValue}
          />
        </SelectContainer>
      </Modal>
    </PictureDataContext.Provider>
  );
};

export const usePictureData = () => {
  const context = useContext(PictureDataContext);
  if (!context) {
    throw new Error("usePictureData must be used within a PictureProvider");
  }
  return context;
};
